// Saved page functionality

import { marked } from "marked";
import { tr } from "./translations.js";
import { toggleStarred, getStarredItems } from "./state.js";

// Types
interface Prompt {
  match?: string;
  text: string;
}

interface PromptsGroup {
  title?: string;
  match?: string;
  prompts: Prompt[];
}

interface TechniqueExample {
  label?: string;
  text: string;
}

interface ResearchSource {
  ref: string;
  summary: string;
}

interface ListItem {
  ref: string;
  title: string;
}

interface ListGroup {
  title: string;
  items: ListItem[];
}

interface ListCard {
  listType: string;
  header: string;
  description: string;
  items?: ListItem[];
  groups?: ListGroup[];
}

interface AllCards {
  topics: Record<string, { guidance: string; promptGroups?: PromptsGroup[]; sources?: ResearchSource[] }>;
  blockers: Record<string, { reassurance: string; strategies?: { ref: string; label: string }[]; sources?: ResearchSource[] }>;
  strategies: Record<string, { guidance: string; promptGroups?: PromptsGroup[]; sources?: ResearchSource[] }>;
  techniques: Record<string, { guidance: string; examples?: TechniqueExample[]; promptGroups?: PromptsGroup[]; sources?: ResearchSource[] }>;
  lists: Record<string, ListCard>;
  [key: string]: unknown;
}

// Types for saved items
interface SavedTopic {
  id: string;
  title: string;
  content: string;
  promptGroups?: PromptsGroup[];
}

interface SavedStrategy {
  id: string;
  label: string;
  content: string;
  promptGroups?: PromptsGroup[];
}

interface SavedBlocker {
  id: string;
  title: string;
  content: string;
  strategies: SavedStrategy[];
}

interface SavedTechnique {
  id: string;
  title: string;
  content: string;
  promptGroups?: PromptsGroup[];
  examples?: TechniqueExample[];
}

// Helper to get list card by type
function getListByType(cards: AllCards, listType: string): ListCard | undefined {
  return Object.values(cards.lists).find(list => list.listType === listType);
}

// Render prompts for saved page (show all, no filtering)
function renderPromptGroups(promptGroups: PromptsGroup[] | undefined): string {
  if (!promptGroups || promptGroups.length === 0) return '';

  return promptGroups.map(group => {
    const titleHtml = group.title
      ? `<p class="text-sm font-bold text-gray-900 mt-4 mb-1">${group.title}</p>`
      : '';

    const promptsHtml = group.prompts.map(p => `<li>${p.text}</li>`).join('');

    return `
      <div>
        ${titleHtml}
        <div class="ml-4 pl-4 border-l-2 border-l-lime-500 ${group.title ? '' : 'mt-4'}">
          <ul class="space-y-2 text-sm text-lime-800">
            ${promptsHtml}
          </ul>
        </div>
      </div>
    `;
  }).join('');
}

// Render technique examples
function renderExamples(examples: TechniqueExample[] | undefined): string {
  if (!examples || examples.length === 0) return '';
  return `
    <div class="mt-4 pt-4 border-t border-gray-100">
      <p class="text-sm font-medium text-gray-700 mb-2">${tr().examplesLabel}</p>
      <div class="space-y-2">
        ${examples.map(ex => `
          <div class="bg-gray-50 rounded-lg px-3 py-2 text-sm">
            ${ex.label ? `<span class="text-gray-500">${ex.label}</span> ` : ''}
            <span class="text-gray-800">${ex.text}</span>
          </div>
        `).join('')}
      </div>
    </div>
  `;
}

// Initialize the saved page
export function initSavedPage(): void {
  const listEl = document.getElementById("saved-list");
  const emptyEl = document.getElementById("saved-empty-state");
  const cardsDataEl = document.getElementById("cards-data");

  if (!listEl || !cardsDataEl) return;

  const cards: AllCards = JSON.parse(cardsDataEl.textContent || "{}");
  const starred = getStarredItems();

  if (starred.size === 0) {
    listEl.classList.add("hidden");
    emptyEl?.classList.remove("hidden");
    return;
  }

  emptyEl?.classList.add("hidden");
  listEl.classList.remove("hidden");

  // Get list cards for ordering
  const topicsList = getListByType(cards, "topics");
  const blockersList = getListByType(cards, "blockers");
  const techniquesList = getListByType(cards, "techniques");

  // Collect saved items by type
  const savedTopics: SavedTopic[] = [];
  const savedBlockers: SavedBlocker[] = [];
  const savedTechniques: SavedTechnique[] = [];

  // Process topics
  if (topicsList) {
    const allTopicItems = topicsList.groups
      ? topicsList.groups.flatMap(g => g.items)
      : (topicsList.items || []);

    for (const item of allTopicItems) {
      const cardId = item.ref.replace(/\.card$/, "").replace(/\//g, "_");
      if (starred.has(`guidance_${cardId}`)) {
        const topic = cards.topics[cardId];
        if (topic) {
          savedTopics.push({
            id: cardId,
            title: item.title,
            content: topic.guidance,
            promptGroups: topic.promptGroups,
          });
        }
      }
    }
  }

  // Process blockers and strategies
  if (blockersList?.items) {
    for (const item of blockersList.items) {
      const cardId = item.ref.replace(/\.card$/, "").replace(/\//g, "_");
      const blocker = cards.blockers[cardId];
      if (!blocker) continue;

      const blockerStarred = starred.has(`guidance_${cardId}`);

      // Check for starred strategies
      const savedStrategies: SavedStrategy[] = [];
      if (blocker.strategies) {
        for (const stratRef of blocker.strategies) {
          const stratId = stratRef.ref
            .replace("blockers/strategies/", "blockers_strategies_")
            .replace(".card", "");
          if (starred.has(`guidance_${stratId}`)) {
            const strategy = cards.strategies[stratId];
            if (strategy) {
              savedStrategies.push({
                id: stratId,
                label: stratRef.label,
                content: strategy.guidance,
                promptGroups: strategy.promptGroups,
              });
            }
          }
        }
      }

      // Include blocker if it's starred or has starred strategies
      if (blockerStarred || savedStrategies.length > 0) {
        savedBlockers.push({
          id: cardId,
          title: item.title,
          content: blockerStarred ? blocker.reassurance : "",
          strategies: savedStrategies,
        });
      }
    }
  }

  // Process techniques
  if (techniquesList?.items) {
    for (const item of techniquesList.items) {
      const cardId = item.ref.replace(/\.card$/, "").replace(/\//g, "_");
      if (starred.has(`guidance_${cardId}`)) {
        const technique = cards.techniques[cardId];
        if (technique) {
          savedTechniques.push({
            id: cardId,
            title: item.title,
            content: technique.guidance,
            promptGroups: technique.promptGroups,
            examples: technique.examples,
          });
        }
      }
    }
  }

  // Render the saved items
  let html = "";

  // Copy button with description
  html += `
    <div class="bg-gray-50 rounded-lg p-4 mb-6">
      <div class="flex items-start justify-between gap-4">
        <div class="text-sm text-gray-600">
          <p><strong>${tr().copyWorksheet}</strong> ${tr().copyWorksheetDescription}</p>
        </div>
        <button id="copy-worksheet-btn" class="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 whitespace-nowrap flex-shrink-0">
          ${tr().copyWorksheet}
        </button>
      </div>
    </div>
  `;

  if (savedTopics.length > 0) {
    html += `
      <div class="border-2 border-[#52C9E8] rounded-lg bg-white p-4">
        <div class="flex items-center gap-3 mb-4">
          <img src="/icon-topic.png" alt="" class="w-8 h-8" />
          <h2 class="text-lg font-semibold text-gray-900">Topics</h2>
        </div>
        <div class="space-y-3">
          ${savedTopics.map(t => `
            <div class="border border-gray-200 rounded-lg p-4" data-item-id="${t.id}">
              <div class="flex items-center justify-between mb-2">
                <h3 class="font-medium text-gray-900">${t.title}</h3>
                <button class="btn-star text-xs px-2 py-1 rounded border border-yellow-400 bg-yellow-50 text-yellow-700">
                  ${tr().starred}
                </button>
              </div>
              <div class="prose prose-gray prose-sm max-w-none">${marked.parse(t.content)}</div>
              ${renderPromptGroups(t.promptGroups)}
            </div>
          `).join("")}
        </div>
      </div>
    `;
  }

  if (savedBlockers.length > 0) {
    html += `
      <div class="border-2 border-amber-500 rounded-lg bg-white p-4 mt-4">
        <div class="flex items-center gap-3 mb-4">
          <img src="/icon-blocker.png" alt="" class="w-8 h-8" />
          <h2 class="text-lg font-semibold text-gray-900">Getting Unstuck</h2>
        </div>
        <div class="space-y-3">
          ${savedBlockers.map(b => `
            <div class="border border-gray-200 rounded-lg p-4" data-item-id="${b.id}">
              <div class="flex items-center justify-between mb-2">
                <h3 class="font-medium text-gray-900">${b.title}</h3>
                ${b.content ? `
                  <button class="btn-star text-xs px-2 py-1 rounded border border-yellow-400 bg-yellow-50 text-yellow-700">
                    ${tr().starred}
                  </button>
                ` : ""}
              </div>
              ${b.content ? `<div class="prose prose-gray prose-sm max-w-none mb-4">${marked.parse(b.content)}</div>` : ""}
              ${b.strategies.length > 0 ? `
                <div class="space-y-2 ${b.content ? "border-t border-gray-200 pt-4" : ""}">
                  ${b.strategies.map(s => `
                    <div class="bg-gray-50 rounded-lg p-3" data-item-id="${s.id}">
                      <div class="flex items-center justify-between mb-2">
                        <span class="font-medium text-gray-700 text-sm">${s.label}</span>
                        <button class="btn-star text-xs px-2 py-1 rounded border border-yellow-400 bg-yellow-50 text-yellow-700">
                          ${tr().starred}
                        </button>
                      </div>
                      <div class="prose prose-gray prose-sm max-w-none">${marked.parse(s.content)}</div>
                      ${renderPromptGroups(s.promptGroups)}
                    </div>
                  `).join("")}
                </div>
              ` : ""}
            </div>
          `).join("")}
        </div>
      </div>
    `;
  }

  if (savedTechniques.length > 0) {
    html += `
      <div class="border-2 border-purple-400 rounded-lg bg-white p-4 mt-4">
        <div class="flex items-center gap-3 mb-4">
          <img src="/icon-technique.png" alt="" class="w-8 h-8" />
          <h2 class="text-lg font-semibold text-gray-900">Techniques</h2>
        </div>
        <div class="space-y-3">
          ${savedTechniques.map(t => `
            <div class="border border-gray-200 rounded-lg p-4" data-item-id="${t.id}">
              <div class="flex items-center justify-between mb-2">
                <h3 class="font-medium text-gray-900">${t.title}</h3>
                <button class="btn-star text-xs px-2 py-1 rounded border border-yellow-400 bg-yellow-50 text-yellow-700">
                  ${tr().starred}
                </button>
              </div>
              <div class="prose prose-gray prose-sm max-w-none">${marked.parse(t.content)}</div>
              ${renderExamples(t.examples)}
              ${renderPromptGroups(t.promptGroups)}
            </div>
          `).join("")}
        </div>
      </div>
    `;
  }

  listEl.innerHTML = html;

  // Set up unstar handlers
  listEl.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains("btn-star")) {
      const itemEl = target.closest("[data-item-id]") as HTMLElement;
      if (itemEl) {
        const itemId = itemEl.dataset.itemId;
        if (itemId) {
          toggleStarred(`guidance_${itemId}`);
          // Re-render the page
          initSavedPage();
        }
      }
    }
  });

  // Set up copy worksheet handler
  const copyBtn = document.getElementById("copy-worksheet-btn");
  if (copyBtn) {
    copyBtn.addEventListener("click", async () => {
      const worksheetText = generateWorksheetText(savedTopics, savedBlockers, savedTechniques);
      try {
        await navigator.clipboard.writeText(worksheetText);
        copyBtn.textContent = tr().copied || "Copied!";
        setTimeout(() => {
          copyBtn.textContent = tr().copyWorksheet || "Copy as worksheet";
        }, 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    });
  }
}

// Generate worksheet text with > prefix on each line
function generateWorksheetText(
  topics: SavedTopic[],
  blockers: SavedBlocker[],
  techniques: SavedTechnique[]
): string {
  const lines: string[] = [];

  // Add a line with > prefix (keeps paragraph as single line)
  const addLine = (text: string) => {
    // Collapse any internal newlines to spaces (keep paragraphs as single lines)
    const singleLine = text.replace(/\n/g, ' ').trim();
    if (singleLine) {
      lines.push('> ' + singleLine);
    }
  };

  // Add instructions at the top
  lines.push('> All the ideas and prompts from the worksheet start with > like this line');
  lines.push('> Delete the instructions and prompts as you go along!');
  lines.push('');
  lines.push('> ---');
  lines.push('');

  // Topics
  if (topics.length > 0) {
    addLine('## Topics');
    lines.push('');
    for (const topic of topics) {
      addLine(`### ${topic.title}`);
      lines.push('');
      addLine(topic.content);
      lines.push('');

      if (topic.promptGroups) {
        for (const group of topic.promptGroups) {
          if (group.title) {
            addLine(`**${group.title}**`);
          }
          for (const prompt of group.prompts) {
            addLine(`- ${prompt.text}`);
          }
          lines.push('');
        }
      }

      // Space for writing
      lines.push('');
      lines.push('');
    }
  }

  // Blockers
  if (blockers.length > 0) {
    lines.push('> ---');
    lines.push('');
    addLine('## Getting Unstuck');
    lines.push('');
    for (const blocker of blockers) {
      addLine(`### ${blocker.title}`);
      lines.push('');
      if (blocker.content) {
        addLine(blocker.content);
        lines.push('');
      }

      for (const strategy of blocker.strategies) {
        addLine(`**${strategy.label}**`);
        lines.push('');
        addLine(strategy.content);
        lines.push('');

        if (strategy.promptGroups) {
          for (const group of strategy.promptGroups) {
            if (group.title) {
              addLine(`*${group.title}*`);
            }
            for (const prompt of group.prompts) {
              addLine(`- ${prompt.text}`);
            }
            lines.push('');
          }
        }
      }

      lines.push('');
    }
  }

  // Techniques
  if (techniques.length > 0) {
    lines.push('> ---');
    lines.push('');
    addLine('## Techniques');
    lines.push('');
    for (const technique of techniques) {
      addLine(`### ${technique.title}`);
      lines.push('');
      addLine(technique.content);
      lines.push('');

      if (technique.examples && technique.examples.length > 0) {
        addLine('**Examples:**');
        for (const ex of technique.examples) {
          const label = ex.label ? `${ex.label}: ` : '';
          addLine(`- ${label}${ex.text}`);
        }
        lines.push('');
      }

      if (technique.promptGroups) {
        for (const group of technique.promptGroups) {
          if (group.title) {
            addLine(`**${group.title}**`);
          }
          for (const prompt of group.prompts) {
            addLine(`- ${prompt.text}`);
          }
          lines.push('');
        }
      }

      lines.push('');
    }
  }

  return lines.join('\n');
}

// Initialize saved link in header
export function initSavedHeaderLink(): void {
  const starred = getStarredItems();
  if (starred.size === 0) return;

  // Find the header
  const header = document.querySelector("header");
  if (!header) return;

  // Find the language switch link (we'll insert before it)
  const langSwitch = header.querySelector("[data-lang-switch]");
  if (!langSwitch) return;

  // Get base path for current language
  const lang = document.body.dataset.lang || "en";
  const basePath = lang === "en" ? "" : `/${lang}`;

  // Create the saved link
  const savedLink = document.createElement("a");
  savedLink.href = `${basePath}/saved`;
  savedLink.className = "text-sm text-yellow-600 hover:text-yellow-700 no-underline mr-4";
  savedLink.textContent = tr().savedHeaderLink;

  // Insert before the language switch
  langSwitch.parentElement?.insertBefore(savedLink, langSwitch);
}
